import { useMemo } from 'react';
import type { POI } from './useMapPOIs';

export interface POIWithDistance extends POI {
  distanceMeters: number;
  distanceText: string;
  drivingDurationSeconds: number;
  drivingDurationText: string;
  bicyclingDurationSeconds: number;
  bicyclingDurationText: string;
  motorcycleDurationSeconds: number;
  motorcycleDurationText: string;
}

// Earth's radius in meters
const R = 6371e3;

function toRad(value: number) {
  return (value * Math.PI) / 180;
}

/**
 * Calculates straight-line distance using Haversine formula
 */
function getHaversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const phi1 = toRad(lat1);
  const phi2 = toRad(lat2);
  const deltaPhi = toRad(lat2 - lat1);
  const deltaLambda = toRad(lon2 - lon1);

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
            Math.cos(phi1) * Math.cos(phi2) *
            Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

function formatDuration(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h} h ${rem} min` : `${h} h`;
}

/**
 * Hook to instantly compute distances and estimated travel times to all POIs
 * without hitting Google API for all 30+ items at once.
 */
export function usePOIDistances(
  pois: POI[],
  origin: { lat: number; lng: number } | null
): POIWithDistance[] {
  return useMemo(() => {
    if (!origin || !pois.length) return pois as POIWithDistance[];

    return pois.map(poi => {
      // 1. Calculate precise straight line distance
      const straightDistance = getHaversineDistance(
        origin.lat, origin.lng,
        poi.position.lat, poi.position.lng
      );
      
      // 2. Add routing penalty (roads are rarely straight). Typical urban routing factor is 1.3 to 1.4.
      const routeDistance = straightDistance * 1.35;

      // 3. Estimate travel speeds in an urban context
      // Car: ~30 km/h average in city traffic (8.3 m/s)
      const drivingSpeed = 8.3;
      const drivingDurationSeconds = routeDistance / drivingSpeed;

      // Motorcycle: ~40 km/h average (lane filtering) (11.1 m/s) -> ~75% of car time
      const motorcycleSpeed = 11.1;
      const motorcycleDurationSeconds = routeDistance / motorcycleSpeed;

      // Bicycle: ~15 km/h average (4.1 m/s)
      const bicyclingSpeed = 4.1;
      const bicyclingDurationSeconds = routeDistance / bicyclingSpeed;

      return {
        ...poi,
        distanceMeters: routeDistance,
        distanceText: formatDistance(routeDistance),
        drivingDurationSeconds,
        drivingDurationText: formatDuration(drivingDurationSeconds),
        motorcycleDurationSeconds,
        motorcycleDurationText: formatDuration(motorcycleDurationSeconds),
        bicyclingDurationSeconds,
        bicyclingDurationText: formatDuration(bicyclingDurationSeconds),
      };
    });
  }, [pois, origin?.lat, origin?.lng]);
}
